import { useEffect, useState } from "react";
import EntryList from "components/pool-details/entries/entry-list";
import { Entry } from "interfaces/entry";
import { PoolDetailsResponse } from "interfaces/pool-details-response";
import { getRequest } from "utils/httpClient";
import Header from "components/header";
import Headroom from "react-headroom";
import LoadingSpinner from "components/loading-spinner.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupState, setGroup } from "state/slices/group-slice";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import LoginButtons from "components/login-buttons";
import { getGroupDetailsPath, getPoolPath } from "utils/backend-path-builders";
import { getContestPagePath } from "utils/contest-group-utils";
import useQueryParams from "utils/useQueryParams";
import { local as localStorage } from "utils/local-storage";
import SuccessfulBetModal from "components/pool-details/successful-bet-modal";
import { Bet } from "interfaces/bet";
import PoolSummary from "./pool-summary";
import { BetTypeName } from "interfaces/bet-type-name";
import { ContestantResponse } from "interfaces/contestant-response";
import { sortBetTypes } from "components/pool-details/bet-types";
import { findFirst } from "utils/array-utils";
import { Navigate, useParams } from "react-router-dom";
import {
  addOptimisticContestPick,
  fetchBetsForContestEntry,
  getContestById,
  getContestIdByEntryId,
  refreshUserContestEntry,
  setContestCurrentEntry,
} from "state/slices/contests-slice";
import useContestData from "hooks/useContestData.ts";
import { CONTEST_ENTRY_ID_QUERY_PARAM, CONTEST_ID_QUERY_PARAM } from "utils/constants.ts";
import { Helmet } from "react-helmet-async";
import { clearContestBanner, setContestBanner, } from "state/slices/adContestSlice.ts";

const PoolPage = () => {
  const dispatch = useAppDispatch();
  const { loading: loadingContest } = useContestData();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [contestants, setContestants] = useState<ContestantResponse[]>([]);
  const [pool, setPool] = useState<PoolDetailsResponse>(
    {} as PoolDetailsResponse
  );
  const group = useAppSelector(getGroupState);

  const [successfulBet, setSuccessfulBet] = useState<Bet | undefined>();
  const [currentPoolType, setCurrentPoolType] = useState<BetTypeName>();
  const { refetchUserContests } = useContestData();

  const isContestGroup = group.isContestGroup;
  const onSuccessfulBetPlaced = (bet: Bet) => {
    setSuccessfulBet(bet);
    setLoading(true);
    if (isContestGroup) {
      refetchUserContests();
      if (bet.contest_entry_id) {
        // Reflect the pick locally first so the contestant greys out and picks
        // remaining drop immediately; the refetch below reconciles afterwards.
        dispatch(addOptimisticContestPick({ contestEntryId: bet.contest_entry_id, bet }));
        dispatch(fetchBetsForContestEntry(bet.contest_entry_id));
        dispatch(refreshUserContestEntry(bet.contest_entry_id));
      }
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    const path = getPoolPath(group.id, id);
    getRequest(path, { skipIntegrationApi: true }).then(
      (pool: PoolDetailsResponse) => {
        setEntries(pool.entries);
        setContestants(pool.contestants);
        setCurrentPoolType(findFirst(sortBetTypes(pool.pool_types)));
        setPool(pool);

        if (pool.group_id && pool.group_id !== group.id) {
          const path = getGroupDetailsPath(pool.group_id);
          getRequest(path, { skipIntegrationApi: true }).then((data) => {
            dispatch(setGroup(data));
          });
        }

        setLoading(false);
      }
    );
  }, [dispatch, group.id, id, loading]);

  const queryParams = useQueryParams();
  const referrerID = queryParams.get("utm_source");
  const referrerGroupID = queryParams.get("utm_campaign");
  const referrerMedium = queryParams.get("utm_medium");

  if (referrerGroupID != null) {
    if (referrerID != null) {
      localStorage.setItem("referrer-id-" + referrerGroupID, `${referrerID}`);
    }
    if (referrerMedium != null) {
      localStorage.setItem(
        "referrer-medium-" + referrerGroupID,
        `${referrerMedium}`
      );
    }
  }

  const contestEntryId = queryParams.get(CONTEST_ENTRY_ID_QUERY_PARAM) || undefined

  const contestIdFromContestEntryId =
    useAppSelector((state) =>
      getContestIdByEntryId(state, contestEntryId || "")
    ) ?? undefined;

  const contestId = queryParams.get(CONTEST_ID_QUERY_PARAM) ?? contestIdFromContestEntryId ?? "";

  const contest = useAppSelector((state) => getContestById(state, contestId));

  const backUrl =
    isContestGroup && contest ? getContestPagePath(contestId, group.id) : "/";


  useEffect(() => {
    if (group.id && contestEntryId) {
      dispatch(fetchBetsForContestEntry(contestEntryId));
    }
  }, [contestEntryId, group.id, dispatch]);

  useEffect(() => {
    if (contestEntryId) {
      dispatch(setContestCurrentEntry({ contestId: contestId, entryId: contestEntryId }));
    }
  }, [contestEntryId, contestId, dispatch]);

  useEffect(() => {
    if (contest && contest.settings?.ad_banner?.enabled) {
      dispatch(setContestBanner(contest.settings.ad_banner));
    }

    return () => {
      dispatch(clearContestBanner());
    };
  }, [contest, dispatch]);

  if (loading || loadingContest) {
    return <LoadingSpinner />;
  }

  if (!id) {
    return <Navigate to="/error" />;
  }

  return (
    <>
      <Helmet>
        <meta property="og:title" content={`${pool.event_name}:${pool.name}`} />
        <meta property="og:description" content={pool.win_condition} />
      </Helmet>
      <Headroom>
        <Header />
        <PoolSummary
          pool={pool}
          betType={currentPoolType}
          backUrl={backUrl}
          contest={contest}
        />
        <LoginButtons />
      </Headroom>

      {(contestants && contestants.length >= 1) ||
      (entries && entries.length >= 1) ? (
        <EntryList
          pool={pool}
          setCurrentPoolType={setCurrentPoolType}
          onSuccessfulBetPlaced={onSuccessfulBetPlaced}
          contest={contest}
          contestEntryId={contestEntryId}
        />
      ) : (
        <BlockyHeavyText
          style={{
            color: "white",
            display: "flex",
            justifyContent: "center",
            marginTop: 15,
          }}
        >
          This pool has no contestants
        </BlockyHeavyText>
      )}
      {successfulBet && (
        <SuccessfulBetModal
          bet={successfulBet}
          close={() => setSuccessfulBet(undefined)}
        />
      )}
    </>
  );
};

export default PoolPage;
